var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo, useState } from "react";
import { Segmented, Select } from "antd";
import { CompsChart } from "@ms/shared-components";
import { CHANNEL_INFO_CHART_SETTINGS } from "./config/index";
import { AXIS_LABEL_INTEGER } from "../../configs/tooltipCommon";
import { CHANNEL_SORT_OPTIONS } from "../../configs/selectOptionsConfig";
import { EmptyDataPlaceholder } from "../EmptyDataPlaceholder";
import { pickChannelInfoChart, } from "../../hooks/useChannelInfoData";
var CHANNEL_LABELS = [
    "前台",
    "中央",
    "小程序",
    "官网",
    "OTA直连",
    "企业直连",
];
function ensureCategoriesAreLabels(categories) {
    if (!(categories === null || categories === void 0 ? void 0 : categories.length))
        return [];
    var looksNumeric = categories.every(function (c) {
        return typeof c === "number" ||
            (typeof c === "string" && /^-?\d+(\.\d+)?$/.test(c));
    });
    var labels = looksNumeric
        ? categories.map(function (_, i) { var _a; return (_a = CHANNEL_LABELS[i]) !== null && _a !== void 0 ? _a : "\u6E20\u9053".concat(i + 1); })
        : categories.map(String);
    return labels;
}
var DEFAULT_CHART_DATA = {
    categories: [],
    dataset: { volume: [], customerUnitPrice: [] },
    axes: {
        left: {
            id: "left",
            position: "left",
            name: "订单量",
            unit: "单",
            min: 0,
            max: 220,
            axisLabel: AXIS_LABEL_INTEGER,
        },
        right: {
            id: "right",
            position: "right",
            name: "客单价",
            unit: "元",
            min: 0,
            max: 3100,
            axisLabel: AXIS_LABEL_INTEGER,
        },
    },
    series: [],
};
var ChannelInfoChart = function (_a) {
    var style = _a.style, bundle = _a.bundle, _b = _a.loading, loading = _b === void 0 ? false : _b, onTitleClick = _a.onTitleClick, titleLeftAdornment = _a.titleLeftAdornment;
    var _c = useState("7d"), range = _c[0], setRange = _c[1];
    var _d = useState("按客单价排序"), sortType = _d[0], setSortType = _d[1];
    var baseChartData = useMemo(function () {
        var _a;
        var raw = (_a = pickChannelInfoChart(bundle, range)) !== null && _a !== void 0 ? _a : DEFAULT_CHART_DATA;
        var labels = ensureCategoriesAreLabels(raw.categories);
        var categories;
        if (labels.length === CHANNEL_LABELS.length) {
            categories = __spreadArray([], CHANNEL_LABELS, true);
        }
        else if (labels.length > 0) {
            categories = __spreadArray([], labels, true);
        }
        else {
            categories = [];
        }
        return __assign(__assign({}, raw), { categories: categories, dataset: raw.dataset ? __assign({}, raw.dataset) : raw.dataset });
    }, [bundle, range]);
    var channelInfoChartData = useMemo(function () {
        var data = __assign({}, baseChartData);
        var categories = data.categories, dataset = data.dataset;
        if (!categories || !(dataset === null || dataset === void 0 ? void 0 : dataset.volume) || !(dataset === null || dataset === void 0 ? void 0 : dataset.customerUnitPrice)) {
            return data;
        }
        var indices = Array.from({ length: categories.length }, function (_, i) { return i; });
        if (sortType === "按客单价排序") {
            indices.sort(function (a, b) { return dataset.customerUnitPrice[b] - dataset.customerUnitPrice[a]; });
        }
        else if (sortType === "按订单量排序") {
            indices.sort(function (a, b) { return dataset.volume[b] - dataset.volume[a]; });
        }
        var axes = data.axes;
        var withLabel = function (ax) {
            return ax && typeof ax === "object"
                ? __assign(__assign({}, ax), { axisLabel: AXIS_LABEL_INTEGER }) : ax;
        };
        var bottomAxis = (axes === null || axes === void 0 ? void 0 : axes.bottom) != null && typeof axes.bottom === "object"
            ? __assign(__assign({}, axes.bottom), { axisLabel: { interval: 0, show: true } }) : {
            id: "bottom",
            position: "bottom",
            axisLabel: { interval: 0, show: true },
        };
        var normalizedAxes = axes
            ? __assign(__assign(__assign(__assign({}, axes), (axes.left == null ? {} : { left: withLabel(axes.left) })), (axes.right == null ? {} : { right: withLabel(axes.right) })), { bottom: bottomAxis }) : data.axes;
        var sortedCategories = indices.map(function (i) { var _a; return (_a = CHANNEL_LABELS[i]) !== null && _a !== void 0 ? _a : "\u6E20\u9053".concat(i + 1); });
        return __assign(__assign({}, data), { categories: __spreadArray([], sortedCategories, true), dataset: {
                volume: indices.map(function (i) { return dataset.volume[i]; }),
                customerUnitPrice: indices.map(function (i) { return dataset.customerUnitPrice[i]; }),
            }, axes: normalizedAxes, series: data.series });
    }, [baseChartData, sortType]);
    var hasChartData = channelInfoChartData &&
        Array.isArray(channelInfoChartData.series) &&
        channelInfoChartData.series.length > 0 &&
        Array.isArray(channelInfoChartData.categories) &&
        channelInfoChartData.categories.length > 0;
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: "\n          .channel-info-header-selects .ant-select {\n            width: auto !important;\n            min-width: 140px !important;\n          }\n        " }), _jsxs("div", __assign({ "data-component-id": "chart:channel-info", style: __assign({ display: "flex", flexDirection: "column", width: 400, height: 300, border: "1px solid #f0f0f0", borderRadius: 4, background: "#fafafa", boxSizing: "border-box", overflow: "visible", flexShrink: 0 }, style) }, { children: [_jsxs("div", __assign({ style: {
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 12px",
                            borderBottom: "1px solid #f0f0f0",
                            background: "#fff",
                            flexShrink: 0,
                        } }, { children: [_jsxs("div", __assign({ style: { display: "flex", alignItems: "center", flexShrink: 0, minWidth: 0 } }, { children: [titleLeftAdornment && (_jsx("div", __assign({ style: {
                                            marginLeft: -4,
                                            marginRight: 4,
                                            flexShrink: 0,
                                            display: "flex",
                                            alignItems: "center",
                                        } }, { children: titleLeftAdornment }))), onTitleClick ? (_jsx("button", __assign({ type: "button", onClick: onTitleClick, style: {
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: "#333",
                                            cursor: "pointer",
                                            padding: 0,
                                            border: "none",
                                            background: "none",
                                        }, title: "\u70B9\u51FB\u67E5\u770B\u6E20\u9053\u6536\u5165" }, { children: "\u6E20\u9053\u6536\u5165" }))) : (_jsx("div", __assign({ style: { fontSize: 14, fontWeight: 500, color: "#333" } }, { children: "\u6E20\u9053\u6536\u5165" })))] })), _jsxs("div", __assign({ className: "channel-info-header-selects", style: { display: "flex", alignItems: "center", gap: 8 } }, { children: [_jsx(Segmented, { size: "small", value: range, onChange: function (v) { return setRange(v); }, options: [
                                            { label: "7天", value: "7d" },
                                            { label: "30天", value: "30d" },
                                            { label: "12个月", value: "12m" },
                                        ] }), _jsx(Select, { value: sortType, onChange: setSortType, size: "small", popupMatchSelectWidth: false, options: CHANNEL_SORT_OPTIONS })] }))] })), _jsx("div", __assign({ style: {
                            flex: 1,
                            minHeight: 0,
                            width: "100%",
                            background: "#fff",
                            display: "flex",
                        } }, { children: loading ? (_jsx(EmptyDataPlaceholder, { description: "\u52A0\u8F7D\u4E2D\u2026" })) : hasChartData ? (_jsx(CompsChart, { type: "combo", data: channelInfoChartData, settings: CHANNEL_INFO_CHART_SETTINGS, style: { width: "100%", height: "100%" } }, "channel-info-".concat(range))) : (_jsx(EmptyDataPlaceholder, { description: "\u6682\u65E0\u6570\u636E" })) }))] }))] }));
};
export default ChannelInfoChart;
